import { Component, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { FormField } from '../../../forms/formField';
import {
    rejectAmericanExpress,
    validateCardNumber
} from '../../../forms/helpers/validation';
import {
    normalizeCardNumber,
    normalizeCVV,
    normalizeExpiryDate
} from '../../../helpers/normalizers';
import { CLOSE_MODAL } from '../../../iwModals/Modals.actions';
import { AutomaticRepaymentsQuestion } from '../addPaymentMethodModal/AutomaticRepaymentsQuestion';
import { Prompt } from '../../ui/prompt';
import ConfirmationButton from '../../ui/confirmationButton/ConfirmationButton';
import { CancelAction } from '../../ui/confirmationResponse/confirmationQuestion/cancelAction/CancelAction';
import { ConfirmAction } from '../../ui/confirmationResponse/confirmationQuestion/confirmAction/ConfirmAction';
import { ConfirmationQuestion } from '../../ui/confirmationResponse/confirmationQuestion/ConfirmationQuestion';
import { ConfirmationResponse } from '../../ui/confirmationResponse/ConfirmationResponse';
import { ConfirmationStatement } from '../../ui/confirmationResponse/confirmationStatement/ConfirmationStatement';
import promptMessages from '../../Profile/promptSave/PromptSave.messages';
import {
    DELETE_DEBIT_CARD,
    SELECT_REPAYMENT_ROLE,
    UPDATE_DEBIT_CARD
} from './EditPaymentMethodModal.actions';
import styles from './EditPaymentMethodModal.module.css';
import messages from './EditPaymentMethodModal.messages';
import { Dispatch } from 'redux';
import { RootState } from '@iwProvider/configureStore';

const formConfiguration = {
    form: 'editDebitCard'
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    closeModal: () => dispatch({ type: CLOSE_MODAL }),
    deleteDebitCard: (id: string) =>
        dispatch({ type: DELETE_DEBIT_CARD, paymentMethodID: id }),
    updateDebitCard: (debitCardData: {
        paymentMethodID: string;
        repaymentRole: string;
        fundingRole: string;
    }) => dispatch({ type: UPDATE_DEBIT_CARD, ...debitCardData }),
    selectRepaymentRole: (repaymentRole: string) =>
        dispatch({ type: SELECT_REPAYMENT_ROLE, repaymentRole })
});

const mapStateToProps = ({
    editPaymentMethodModal
}: {
    editPaymentMethodModal: RootState['editPaymentMethodModal'];
}) => ({
    ...editPaymentMethodModal,
    initialValues: {
        cardNumber: editPaymentMethodModal.cardNumber,
        expiryDate: editPaymentMethodModal.expiryDateFormatted,
        brand: editPaymentMethodModal.brand,
        repaymentRole: editPaymentMethodModal.repaymentRole
    }
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

class EditPaymentMethodModal extends Component<PropsFromRedux> {
    handleSubmit = () => {
        this.props.deleteDebitCard(this.props.paymentMethodID!);
    };

    handleUpdateDebitCard = () => {
        this.props.updateDebitCard({
            paymentMethodID: this.props.paymentMethodID!,
            repaymentRole: this.props.repaymentRole!,
            fundingRole: this.props.fundingRole!
        });
    };

    render() {
        return (
            <>
                <div className={styles.title}>
                    <FormattedMessage {...messages.editDebitCardTitle} />

                    <ConfirmationResponse
                        className={styles.deletePaymentMethod}
                        onSubmit={this.handleSubmit}
                    >
                        <ConfirmationStatement>
                            <FormattedMessage
                                {...messages.deletePaymentMethod}
                            />
                        </ConfirmationStatement>

                        <ConfirmationQuestion>
                            <FormattedMessage
                                {...messages.deleteConfirmation}
                            />

                            <ConfirmAction>
                                <FormattedMessage {...messages.confirmAction} />
                            </ConfirmAction>
                            <CancelAction>
                                <FormattedMessage {...messages.cancelAction} />
                            </CancelAction>
                        </ConfirmationQuestion>
                    </ConfirmationResponse>
                </div>
                <div
                    className={styles.EditPaymentMethodModal}
                    data-testid="Edit-Payment-Modal"
                >
                    <form
                        className={styles.EditDebitCardForm}
                        autoComplete="off"
                    >
                        <AutomaticRepaymentsQuestion
                            selectRepaymentRole={this.props.selectRepaymentRole}
                        />

                        <div className={styles.formBody}>
                            <FormField
                                name="cardNumber"
                                disabled={true}
                                label={
                                    <FormattedMessage
                                        {...messages.cardNumber}
                                    />
                                }
                                placeholder="XXXX-XXXX-XXXX-XXXX"
                                validate={[
                                    rejectAmericanExpress,
                                    validateCardNumber
                                ]}
                                normalize={normalizeCardNumber}
                                className={styles.cardNumber}
                                {...this.props}
                            />
                            <FormField
                                name="expiryDate"
                                disabled={true}
                                label={
                                    <FormattedMessage
                                        {...messages.expiryDate}
                                    />
                                }
                                placeholder="MM/YY"
                                normalize={normalizeExpiryDate}
                                className={styles.expiryDate}
                            />
                            <FormField
                                name="CVV"
                                placeholder="XXX"
                                disabled={true}
                                label={<FormattedMessage {...messages.CVV} />}
                                normalize={normalizeCVV}
                                className={styles.CVV}
                                {...this.props}
                            />
                            <FormField
                                name="billingPostcode"
                                disabled={true}
                                label={
                                    <FormattedMessage
                                        {...messages.billingPostcode}
                                    />
                                }
                                className={styles.billingPostcode}
                            />
                        </div>
                    </form>
                </div>
                <Prompt className={styles.prompt}>
                    <button onClick={this.props.closeModal}>
                        <FormattedMessage {...promptMessages.cancel} />
                    </button>
                    <ConfirmationButton
                        onClick={this.handleUpdateDebitCard}
                        loading={this.props.updatingDebitCard}
                    >
                        <FormattedMessage {...promptMessages.save} />
                    </ConfirmationButton>
                </Prompt>
            </>
        );
    }
}

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(reduxForm<object, any>(formConfiguration)(EditPaymentMethodModal));
